import { Question, QuestionAnswer } from '@core/types';
import { Input } from '@core/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    question: Question;
    answer: QuestionAnswer | null;
    answerUpdated: (answer: QuestionAnswer) => void;
    classNames?: string;
}

const emptyGuid = '00000000-0000-0000-0000-000000000000';

export const QuestionnaireMultipleChoiceQuestion = ({ question, answer, answerUpdated, classNames }: Props) => {
    const { t } = useTranslation();
    const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
    const [openAnswer, setOpenAnswer] = useState<string>('');

    useEffect(() => {
        setOpenAnswer(answer?.openAnswer ?? '');
        setSelectedAnswers(answer?.selectedAnswers ? [...answer.selectedAnswers] : []);
    }, [answer]);

    const toggleValue = (selectedAnswer: string) => {
        if (selectedAnswers.indexOf(selectedAnswer) !== -1) {
            setSelectedAnswers(selectedAnswers.filter((v) => v !== selectedAnswer));
        } else {
            setSelectedAnswers([...selectedAnswers, selectedAnswer]);
        }
    };

    useEffect(() => {
        if (!selectedAnswers || selectedAnswers.length === 0) {
            answerUpdated({ questionId: question.questionId });
        }

        if (selectedAnswers.indexOf(emptyGuid) !== -1) {
            answerUpdated({ questionId: question.questionId, selectedAnswers: selectedAnswers.sort(), openAnswer: openAnswer });
        } else {
            answerUpdated({ questionId: question.questionId, selectedAnswers: selectedAnswers.sort() });
        }
    }, [selectedAnswers, openAnswer]);

    return (
        <>
            {question.options.map((option, index) => (
                <div key={index} className={`${classNames ? classNames : 'ps-6'}`}>
                    <input
                        type="checkbox"
                        id={`answer${index}`}
                        value={option.optionId}
                        checked={selectedAnswers.indexOf(option.optionId) !== -1}
                        onChange={(e) => toggleValue(option.optionId)}
                        className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-checkbox bg-center bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-sm float-start mt-0.25em -ms-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                    />
                    <label htmlFor={`answer${index}`} className="inline-block">
                        {option.text}
                    </label>
                </div>
            ))}
            {question.hasOtherOption && (
                <div className={`${classNames ? classNames : 'ps-6'}`}>
                    <input
                        type="checkbox"
                        id="answer-other"
                        value={emptyGuid}
                        checked={selectedAnswers.indexOf(emptyGuid) !== -1}
                        onChange={(e) => toggleValue(emptyGuid)}
                        className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-checkbox bg-center bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-sm float-start mt-0.25em -ms-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                    />
                    <label htmlFor={`answer-other`} className="inline-block">
                        {question.otherOptionText}
                    </label>
                    <Input
                        type="text"
                        name="other-value"
                        id={`other-value`}
                        value={openAnswer}
                        onChange={(e) => setOpenAnswer(e.currentTarget.value)}
                        placeholder={t('Questionnaire.OtherAnswer.Placeholder')}
                        classNames="mb-2"
                    />
                </div>
            )}
        </>
    );
};
